// William & Mary: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - W&M's Common Data Set 2025-2026, posted by Institutional Research by
//    section (cds_2025-26_c1.pdf for section C, cds_2025-26_g.pdf for
//    section G) with the full workbook (wm-2025-2026-cds1.xlsx). C1
//    applicants/admits/enrollees for Fall 2025; C8 test policy for Fall
//    2027; C21/C22 early plans; G1/G5 2026-2027 costs (the "not available"
//    box is unchecked, so they are final). Quirks: W&M leaves the C1
//    residency table blank (zeros), so the page shows no in-state split; the
//    section C PDF heads the early decision counts "For the Fall 2024
//    entering class" while the workbook heads the same 1,477 and 735 "For
//    the Fall 2025 entering class" (the 2024-2025 CDS gave 1,586 and 746),
//    so they are read as fall 2025.
//  - W&M Undergraduate Admission: Dates & Deadlines, Early Decision and
//    Standardized Testing. The pages carry no cycle year; they are the pages
//    as posted on 2026-10-01 for the application now open (fall 2027 entry),
//    and the CDS C8 block confirms the test-optional policy for fall 2027.
//  - College Scorecard, school id 231624 (net price by family income), read
//    from the Scorecard API on 2026-10-01; the Scorecard names the school
//    "William & Mary".
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'William & Mary Common Data Set 2025-2026',
        url: 'https://www.wm.edu/offices/ir/university_data/cds/index.php',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'W&M Undergraduate Admission, Dates & Deadlines',
        url: 'https://www.wm.edu/admission/undergraduateadmission/how-to-apply/datesdeadlines/index.php',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'W&M Undergraduate Admission, Early Decision',
        url: 'https://www.wm.edu/admission/undergraduateadmission/how-to-apply/first-year-applicants/earlydecision/index.php',
        read: '2026-10-01',
    },
    tests: {
        label: 'W&M Undergraduate Admission, Standardized Testing',
        url: 'https://www.wm.edu/admission/undergraduateadmission/how-to-apply/first-year-applicants/standardizedtesting/index.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?231624-William-Mary',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'college-of-william-and-mary',
    kbId: 'college_of_william_and_mary',
    name: 'William & Mary',
    shortName: 'W&M',
    aliases: ['College of William and Mary', 'William and Mary', 'W&M'],
    location: 'Williamsburg, VA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (W&M brand color palette); decoration only.
    branding: {
        primary: '#004E38',
        secondary: '#B79257',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/the_college_of_william_and_mary_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 16895,
        admitted: 6245,
        enrolled: 1639,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'W&M\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major, and it leaves the in-state and out-of-state split blank.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II', applied: 1477, admitted: 735 },
        ],
        note: 'The Common Data Set reports both early decision rounds together; they are part of the totals above. W&M has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Early February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'By April 1', binding: false },
        ],
        note: 'The Common App is due by midnight in your time zone; transcripts, test scores and recommendations may still arrive after the deadline. The FAFSA and CSS Profile priority dates are November 15 for Early Decision I, January 15 for Early Decision II and February 1 for Regular Decision. Enrollment deposits are due January 5 for Early Decision I, February 15 for Early Decision II and May 1 for Regular Decision.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if admitted, you commit to enroll the following fall, confirm with a non-refundable deposit and withdraw your other applications. You may apply to other schools, but to only one through a binding early decision process. If your financial situation changes significantly after you apply, you may appeal to the admission committee to be released. A denial in either round is final.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SAT and ACT scores are a completely optional part of the W&M application, and no group of applicants is required to send them. If you send scores, from the testing agency or entered yourself on the application, W&M superscores, and if you send both it uses the one most to your advantage. Every applicant is considered for merit programs with or without scores.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [19734, 46177] },
            { item: 'Required fees', amounts: [7501, 7501] },
            { item: 'Food and housing (on campus)', amounts: [17646, 17646] },
            { item: 'Books and supplies', amounts: [900, 900] },
            { item: 'Transportation', amounts: [785, 785] },
            { item: 'Other expenses', amounts: [2000, 2000] },
        ],
        totals: [48566, 75009],
        note: 'For a student living on campus. The Common Data Set says tuition and fees vary by program, and 12.6 percent of full-time undergraduates pay more than these figures.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 18880,
            byIncome: [
                { income: '$0 to $30,000', amount: 3292 },
                { income: '$30,001 to $48,000', amount: 4385 },
                { income: '$48,001 to $75,000', amount: 6345 },
                { income: '$75,001 to $110,000', amount: 14265 },
                { income: 'Over $110,000', amount: 37032 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from W&M\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
